import { Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { TranslateService } from '@ngx-translate/core';
import { ProductRecipe } from '../../../models/product-recipe/product-recipe';
import { ProductRecipeSearch } from '../../../models/product-recipe/product-recipe-search';
import {
  ProductDevelopmentAdminGroup,
  ProductDevelopmentEditorGroup,
  ProductDevelopmentViewerGroup,
} from '../../../models/authorization/enum/user-group.enum';
import { EnumService } from '../../../services/enum.service';
import { ProductRecipeProxyService } from '../../../proxies/item/product-recipe.proxy.service';
import { ProductDevelopmentStatus } from '@app/models/enums/product-development-status';

@Component({
  selector: 'app-product-recipes',
  templateUrl: './product-recipes.component.html',
  styleUrl: './product-recipes.component.scss',
})
export class ProductRecipesComponent implements OnDestroy {
  private router = inject(Router);
  private productRecipeProxyService = inject(ProductRecipeProxyService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: ProductRecipeSearch = {};

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'product-recipe-filters',
    config: computed(() => (this.enumsReady() ? this.filterConfig() : null)),
  });
  showFilters = signal<boolean>(true);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  listRecipes = signal<ProductRecipe[]>([]);
  statuses = signal<EnumValues>({});
  uoms = signal<EnumValues>({});
  enumsReady = signal(false);

  canEdit = computed(() => {
    if (
      AuthService.hasRole(ProductDevelopmentAdminGroup.ProductRecipeAdmin) ||
      AuthService.hasRole(ProductDevelopmentEditorGroup.ProductRecipeEditor)
    ) {
      return true;
    }
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProductRecipeViewer)) {
      return false;
    }
    return false;
  });

  getRowData(rowData: unknown): ProductRecipe {
    return rowData as ProductRecipe;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const uomValues = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;
        if (Object.keys(uomValues).length > 0) {
          this.uoms.set(uomValues);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: key,
      label: value,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('ProductRecipe_Number', 'Number'),
          placeholder: translate('Enter_recipe_number', 'Enter recipe number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'name',
          label: translate('ProductRecipe_Name', 'Name'),
          placeholder: translate('Enter_recipe_name', 'Enter recipe name'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'outputItemCode',
          label: translate('ProductRecipe_OutputItemCode', 'Item Master Code'),
          placeholder: translate('Enter_output_item_code', 'Enter output item code'),
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('ProductRecipe_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: ProductRecipeSearch): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.productRecipeProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.listRecipes.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.listRecipes.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(values: DynamicFilterValues): void {
    const filters = values as ProductRecipeSearch;
    const searchCriteria: ProductRecipeSearch = {
      ...filters,
      status: filters.status?.map((status) => Number(status)),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  goToDetails(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/product-recipe-details'], { queryParams });
  }

  deletePopup(event: Event, recipe: ProductRecipe) {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'deleteProductRecipe',
      target: event.target as EventTarget,
      message: `${this.translateService.instant('ProductRecipe_Delete_Dialog') || 'Are you sure you want to delete recipe'} ${recipe.number}?`,
      header: this.translateService.instant('Common_Header_Dialog_Danger') || 'Danger',
      icon: 'pi pi-info-circle',
      rejectButtonProps: {
        label: this.translateService.instant('Common_Cancel') || 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('Common_Delete') || 'Delete',
        severity: 'danger',
      },
      accept: () => {
        this.deleteRecipe(recipe);
      },
    });
  }

  deleteRecipe(recipe: ProductRecipe) {
    if (!recipe.id) return;
    this.startLoading();
    this.productRecipeProxyService
      .delete(recipe.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.listRecipes.update((currentRecipes) => currentRecipes.filter((r) => r.id !== recipe.id));
            this.messageService.add({
              severity: 'success',
              summary: `${this.translateService.instant('ProductRecipe') || 'Product Recipe'} ${recipe.number} ${this.translateService.instant('Common_Deleted') || 'deleted'}`,
              key: 'br',
              life: 2500,
            });
          }
        },
      });
  }

  canDelete(recipe: ProductRecipe): boolean {
    return this.canEdit() && recipe.status === ProductDevelopmentStatus.Draft;
  }
}
